/* istanbul ignore file */

import type { ComponentProps } from 'react';
import React, { memo } from 'react';

import type { LocationSearchMachineContext } from '../../machine';
import {
  LocationSearchDeliveryResult,
  LocationSearchLocationsResults,
} from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchResults = memo(
  (props: LocationSearchResultsProps) => {
    const {
      locationSearchType,
      locations,
      deliveryLocation,
      deliverySearchString,
      predictionDeliveryAddresses,
      focusedLocationId,
      isLoadingPredictions,
      isLoadingLocations,
      activeReorderRestaurantSlug,
      minimalCharactersForSearch,
      withSafeAreaInsets,
      onLocationCardPress,
      onLocationCardFocus,
      onDeliveryLocationCardPress,
      onPredictionPress,
      onTryNewAddressBtnPress,
      onChangeToPickupSearchTypeBtnPress,
      onLocationResultsExpandCollapse,
    } = props;

    if (locationSearchType === 'delivery') {
      return (
        <LocationSearchDeliveryResult
          deliveryLocation={deliveryLocation}
          deliverySearchString={deliverySearchString}
          minimalCharactersForSearch={minimalCharactersForSearch}
          predictionDeliveryAddresses={predictionDeliveryAddresses}
          focusedLocationId={focusedLocationId}
          isLoadingPredictions={isLoadingPredictions}
          activeReorderRestaurantSlug={activeReorderRestaurantSlug}
          isLoadingLocation={isLoadingLocations}
          withSafeAreaInsets={withSafeAreaInsets}
          onPredictionPress={onPredictionPress}
          onDeliveryLocationCardPress={onDeliveryLocationCardPress}
          onTryNewAddressBtnPress={onTryNewAddressBtnPress}
          onChangeToPickupSearchTypeBtnPress={
            onChangeToPickupSearchTypeBtnPress
          }
          onLocationResultsExpandCollapse={onLocationResultsExpandCollapse}
        />
      );
    }

    return (
      <LocationSearchLocationsResults
        locationSearchType={locationSearchType}
        locations={locations}
        focusedLocationId={focusedLocationId}
        isLoading={isLoadingLocations}
        activeReorderRestaurantSlug={activeReorderRestaurantSlug}
        withSafeAreaInsets={withSafeAreaInsets}
        onLocationCardPress={onLocationCardPress}
        onLocationCardFocus={onLocationCardFocus}
        onLocationResultsExpandCollapse={onLocationResultsExpandCollapse}
      />
    );
  },
);

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchResultsProps = {
  readonly locationSearchType: LocationSearchMachineContext['locationSearchType'];
  readonly deliverySearchString: LocationSearchMachineContext['deliverySearchString'];
  readonly locations: LocationSearchMachineContext['locations'];
  readonly deliveryLocation: LocationSearchMachineContext['deliveryLocation'];
  readonly minimalCharactersForSearch: number;
  readonly predictionDeliveryAddresses: LocationSearchMachineContext['deliveryAddressPredictions'];
  readonly focusedLocationId: string | undefined;
  readonly activeReorderRestaurantSlug: string | undefined;
  readonly isLoadingPredictions: boolean;
  readonly isLoadingLocations: boolean;
  readonly withSafeAreaInsets?: boolean;
  readonly onPredictionPress: (placeId: string) => void;
  readonly onLocationCardPress: ComponentProps<
    typeof LocationSearchLocationsResults
  >['onLocationCardPress'];
  readonly onLocationCardFocus: ComponentProps<
    typeof LocationSearchLocationsResults
  >['onLocationCardFocus'];
  readonly onDeliveryLocationCardPress: ComponentProps<
    typeof LocationSearchDeliveryResult
  >['onDeliveryLocationCardPress'];
  readonly onTryNewAddressBtnPress: () => void;
  readonly onLocationResultsExpandCollapse: (isExpanded: boolean) => void;
  readonly onChangeToPickupSearchTypeBtnPress: () => void;
};
